import React, { memo, useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { DateTimePicker } from '@atlaskit/datetime-picker';
import type { CSSObjectWithLabel } from '@atlaskit/react-select';
import { token } from '@atlaskit/tokens';

import type { FieldBaseProps } from '../common/types';

import type { JiraDateTimePickerField_Platform$key } from './__generated__/JiraDateTimePickerField_Platform.graphql';
import { BaseDateField, type BaseDateFieldProps } from './BaseDateField';

const messages = defineMessages({
	dateTimePickerLabel: {
		id: 'jira.jira.jira-platform-fields.jira-date-time-picker-field.date-time-picker-label',
		defaultMessage: 'Date and time',
		description: 'Label for the date time picker field',
	},
});

export type Props = FieldBaseProps & {
	field: JiraDateTimePickerField_Platform$key;
	onChange?: (newValue: string | null) => void;
};

// Styles for the individual select components inside the date and time pickers
const selectPropsStyles = {
	control: (base: CSSObjectWithLabel) => ({
		...base,
		// For cleaner UI/borders, leave outline responsibility to outer input element (DateTimePicker)
		boxShadow: 'none',
		// Input height dynamically changes based on presence of value, so we need to set a fixed height
		height: '36px', // 40px - 2*2px outer border
		minHeight: '36px', // overrides default min-height of 40px
	}),
};

export const JiraDateTimePickerField: React.MemoExoticComponent<
	({ field, fieldValue, isFieldEditable, onChange }: Props) => JSX.Element | null
> = memo(({ field, fieldValue, isFieldEditable = true, onChange }: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment<JiraDateTimePickerField_Platform$key>(
		graphql`
			fragment JiraDateTimePickerField_Platform on JiraDateTimePickerField {
				name
				fieldConfig {
					isEditable
					isRequired
				}
				dateTime
			}
		`,
		field,
	);

	const { isRequired } = data?.fieldConfig || {};
	const isEditable = data?.fieldConfig?.isEditable && isFieldEditable;

	const defaultValue = data?.dateTime;
	const currentValue = typeof fieldValue === 'string' ? fieldValue : null;

	const renderPicker = useCallback(
		({ placeholder, ...renderPickerProps }: Parameters<BaseDateFieldProps['renderPicker']>[0]) => (
			<DateTimePicker
				{...renderPickerProps}
				innerProps={{
					style: {
						borderWidth: token('border.width.focused'),
						...(!isRequired || !!currentValue
							? {
									borderColor: token('color.border.focused'),
								}
							: {}),
					},
				}}
				datePickerProps={{
					placeholder,
					label: formatMessage(messages.dateTimePickerLabel),
					selectProps: { styles: selectPropsStyles },
				}}
				timePickerProps={{
					selectProps: { styles: selectPropsStyles },
				}}
			/>
		),
		[formatMessage, isRequired, currentValue],
	);

	return (
		<BaseDateField
			fieldName={data?.name}
			labelText={formatMessage(messages.dateTimePickerLabel)}
			isEditable={!!isEditable}
			isRequired={!!isRequired}
			currentValue={currentValue}
			defaultValue={defaultValue}
			includeTime
			onChange={onChange}
			renderPicker={renderPicker}
		/>
	);
});
